import {
  ShoppingBag,
  Star,
  PhoneCall,
  ShieldCheck,
  Sparkles,
  Users,
  MapPin,
  Mail,
  Quote,
  ArrowRight,
} from "lucide-react";
import HeroBannerSwiper from "@/components/HeroBannerSwiper";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";

const categories = [
  "Workout Plans",
  "Nutrition",
  "Supplements",
  "Fitness Equipment",
  "Personal Training",
  "Yoga Classes",
  "Cardio Programs",
  "Strength Training",
  "Weight Loss",
  "Muscle Building",
  "Meal Plans",
  "Wellness",
];

const bestsellers = [
  {
    title: "Premium Personal Training Package",
    description: "Work one-on-one with certified trainers who design custom workouts based on your fitness level, goals, and schedule. Includes form correction, progress tracking, and motivational support.",
    priceRange: "₹ 2,999 to 8,999",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800",
    variants: ["1 Month", "3 Months", "6 Months"],
    benefits: ["Certified trainers", "Custom workout plans", "Flexible scheduling"],
  },
  {
    title: "Complete Nutrition & Meal Planning",
    description: "Get personalized meal plans crafted by registered nutritionists. Includes macro tracking, grocery lists, recipe suggestions, and weekly check-ins to keep you on track.",
    priceRange: "₹ 1,499 to 4,999",
    image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800",
    variants: ["Basic Plan", "Premium Plan", "VIP Plan"],
    benefits: ["Custom meal plans", "Nutritionist support", "Progress tracking"],
  },
  {
    title: "Home Workout Equipment Bundle",
    description: "Transform any space into your personal gym with our curated equipment bundles. From resistance bands to adjustable dumbbells, get everything you need for effective home workouts.",
    priceRange: "₹ 3,999 to 12,999",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800",
    variants: ["Starter Kit", "Pro Kit", "Elite Kit"],
    benefits: ["Quality equipment", "Space-efficient", "Workout guides included"],
  },
  {
    title: "Online Yoga & Meditation Classes",
    description: "Join live and on-demand yoga sessions led by experienced instructors. Perfect for improving flexibility, reducing stress, and building mindfulness practices into your routine.",
    priceRange: "₹ 999 to 2,999",
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800",
    variants: ["Beginner", "Intermediate", "Advanced"],
    benefits: ["Live sessions", "On-demand library", "Expert instructors"],
  },
  {
    title: "Weight Loss Transformation Program",
    description: "A comprehensive 12-36 week program combining personalized workouts, nutrition coaching, and lifestyle changes. Track your progress with weekly check-ins and celebrate milestones along the way.",
    priceRange: "₹ 4,999 to 14,999",
    image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800",
    variants: ["12 Weeks", "24 Weeks", "36 Weeks"],
    benefits: ["Holistic approach", "Weekly coaching", "Community support"],
  },
];

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "Marketing Executive, Lost 20kg",
    location: "Mumbai",
    quote:
      "FitEasy completely transformed my relationship with fitness. After years of failed gym memberships and fad diets, I finally found something that works. My trainer understood my busy schedule and created workouts I could actually stick to. The nutrition guidance was a game-changer—I learned to eat well without feeling deprived. Six months later, I'm 20kg lighter, stronger, and more confident than ever. This isn't just a fitness app; it's a lifestyle transformation.",
    rating: 5,
  },
  {
    name: "Michael Chen",
    role: "Software Engineer, Strength Training",
    location: "Bangalore",
    quote:
      "As someone who works 12-hour days, I thought fitness was impossible. FitEasy proved me wrong. The online coaching sessions fit perfectly into my schedule, and my trainer adapted workouts for my home setup. I've gained 8kg of muscle in 8 months and can now do things I never thought possible—like deadlifting my body weight! The app's progress tracking keeps me motivated, and the community support is incredible.",
    rating: 5,
  },
  {
    name: "Emma Rodriguez",
    role: "Teacher, Park Class Enthusiast",
    location: "Delhi",
    quote:
      "I love how FitEasy combines everything I need in one place. The park classes are my favorite—there's something magical about working out outdoors with a supportive community. The trainers are knowledgeable, the classes are well-organized, and the QR check-in makes everything seamless. I've made friends, improved my fitness, and actually look forward to my workouts now. FitEasy made fitness fun again!",
    rating: 5,
  },
];

const faqs = [
  {
    question: "How do I get started with FitEasy?",
    answer:
      "Getting started is simple! Download the FitEasy app from the App Store or Google Play, create your free account, and complete our comprehensive fitness assessment. This includes questions about your current fitness level, goals (weight loss, muscle gain, general health), dietary preferences, schedule, and any injuries or limitations. Based on your responses, we'll match you with certified trainers and nutritionists, suggest relevant programs, and create a personalized roadmap to help you achieve your goals. You can start with a free trial or book your first session immediately.",
  },
  {
    question: "Do I need gym equipment to use FitEasy?",
    answer:
      "Not at all! FitEasy is designed to work with whatever you have available. We offer bodyweight-only workouts that require zero equipment, home workout plans that use minimal equipment (like resistance bands or dumbbells), gym-based programs for those with access, and outdoor park classes that use natural elements. You can filter trainers and programs based on your equipment availability, and many trainers specialize in bodyweight training. We also offer equipment bundles if you want to enhance your home setup.",
  },
  {
    question: "Can I track my progress on FitEasy?",
    answer:
      "Absolutely! FitEasy includes comprehensive progress tracking tools. You can log workouts, track sets and reps, record body measurements, upload progress photos, monitor your nutrition and macros, and view detailed analytics showing your strength gains, weight changes, and overall improvements over time. The app generates weekly and monthly reports, and you can share your progress with your trainer or nutritionist for personalized adjustments. Many users find the visual progress tracking particularly motivating.",
  },
  {
    question: "Are the meal plans customizable?",
    answer:
      "Yes, completely! Our registered nutritionists create fully personalized meal plans based on your dietary preferences (vegetarian, vegan, keto, Mediterranean, etc.), food allergies and intolerances, cultural preferences, budget, cooking skills, and lifestyle. Plans include detailed recipes, grocery lists, macro breakdowns, and meal prep tips. You can request modifications anytime through the app, and your nutritionist will adjust your plan based on your progress, feedback, and changing needs. We believe nutrition should fit your life, not the other way around.",
  },
  {
    question: "What makes FitEasy different from other fitness apps?",
    answer:
      "FitEasy combines the best of both worlds: the convenience of technology with the personalization of human expertise. Unlike generic workout apps, you work with real certified trainers and nutritionists who understand your unique situation. Unlike traditional gyms, you have flexibility—train at home, in parks, or at gyms. Our platform integrates everything: workouts, nutrition, progress tracking, and community support, all in one place. Plus, our park classes offer a unique outdoor fitness experience that's hard to find elsewhere.",
  },
  {
    question: "How do I cancel or reschedule a booking?",
    answer:
      "Cancellation and rescheduling policies vary by trainer or class, and all policies are clearly displayed before you confirm your booking. Generally, you can reschedule or cancel sessions up to 24 hours in advance through the app with no penalty. Late cancellations (within 24 hours) may incur a fee, which goes directly to the trainer. For park classes, you can cancel up to 12 hours before class time. We recommend booking with trainers who offer flexible cancellation policies if your schedule is unpredictable. All policies are transparent and agreed upon before payment.",
  },
];

const contactInfo = [
  {
    label: "Email",
    value: "support@fiteasy.com",
    href: "mailto:support@fiteasy.com",
    icon: Mail,
  },
  {
    label: "Phone / WhatsApp",
    value: "+1 (555) 123-4567",
    href: "tel:+15551234567",
    icon: PhoneCall,
  },
  {
    label: "Location",
    value: "123 Fitness Avenue, Health City, HC 12345",
    href: "https://maps.google.com/?q=Fitness+Avenue",
    icon: MapPin,
  },
];

const stats = [
  { label: "100K+", helper: "Active Members" },
  { label: "500+", helper: "Certified Trainers" },
  { label: "1,000+", helper: "Workout Programs" },
  { label: "4.9★", helper: "Average Rating" },
  { label: "50+", helper: "Cities Covered" },
  { label: "95%", helper: "Success Rate" },
];

export default function Home() {
  const organizationData = {
    name: "FitEasy",
    url: "https://fiteasy.com",
    logo: "https://fiteasy.com/logo.png",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-555-123-4567",
      contactType: "Customer Service",
      email: "support@fiteasy.com",
    },
    sameAs: [
      "https://facebook.com/fiteasy",
      "https://twitter.com/fiteasy",
      "https://instagram.com/fiteasy",
    ],
  };

  const serviceData = {
    serviceType: "Fitness Training Service",
    provider: {
      "@type": "Organization",
      name: "FitEasy",
    },
    areaServed: "Worldwide",
    description:
      "Personal training, nutrition coaching, park classes, and online fitness programs",
  };

  const faqStructuredData = {
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="Home"
        description="Transform your fitness journey with FitEasy - personalized workout plans, nutrition guidance, and expert support. Connect with trainers, join park classes, and work with nutritionists all in one app."
        path="/"
      />
      <StructuredData type="Organization" data={organizationData} />
      <StructuredData type="Service" data={serviceData} />
      <StructuredData type="FAQPage" data={faqStructuredData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

      <main id="main-content">
        {/* Hero Section using FitEasy hero assets */}
        <section
          aria-labelledby="hero-heading"
          className="mx-auto flex max-w-6xl flex-col gap-10 px-4 pb-10 pt-10 sm:px-6 lg:px-8 md:flex-row md:items-center"
        >
          <div className="flex-1 space-y-5">
            <p className="text-xs uppercase tracking-[0.35em] text-blue-600 font-semibold">
              All-in-one fitness platform
            </p>
            <h1
              id="hero-heading"
              className="font-display text-3xl sm:text-4xl md:text-5xl text-gray-900"
            >
              Your complete fitness ecosystem—trainers, nutrition, and community, all in one place
            </h1>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              Stop juggling multiple apps and gym memberships. FitEasy brings together certified personal trainers, registered nutritionists, outdoor park classes, and comprehensive progress tracking into one seamless platform. Whether you're training at home, in the gym, or at the park, we've got you covered.
            </p>
            <div className="flex flex-wrap gap-4">
              <CTAButton href="/programs" variant="primary" ariaLabel="Explore all fitness programs">
                Explore Programs
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </CTAButton>
              <CTAButton href="/trainers" variant="outline" ariaLabel="View available trainers">
                View Trainers
                <Star className="h-4 w-4 text-yellow-500" aria-hidden="true" />
              </CTAButton>
            </div>
          </div>
          <div className="relative mt-6 flex-1 md:mt-0" aria-label="FitEasy program highlights">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="relative h-40 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
                <img
                  src="/hero/hero_personal_training.png"
                  alt="Personal training with FitEasy - One-on-one coaching with certified trainers"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="relative h-40 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
                <img
                  src="/hero/hero_home_workout.png"
                  alt="Home workouts with FitEasy - Exercise from anywhere"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="relative h-40 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
                <img
                  src="/hero/hero_yoga.png"
                  alt="Yoga and mobility classes - Flexible movement and mindfulness"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="relative h-40 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
                <img
                  src="/hero/hero_nutrition.png"
                  alt="Nutrition and meal planning - Personalized dietary guidance"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

      {/* Hero Banner Swiper */}
      <div className="w-full">
        <HeroBannerSwiper />
      </div>

      {/* Services Section */}
      <section aria-labelledby="services-heading" className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <SectionHeader
          id="services-heading"
          label="Our Services"
          title="Explore All Services"
          className="mb-8"
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <a
            href="/trainer-discovery"
            className="group rounded-xl border border-blue-200 bg-white p-6 shadow-sm transition hover:shadow-md"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <Users className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900 group-hover:text-blue-600">
              Book a Fitness Trainer for Home
            </h3>
            <p className="text-sm text-gray-600">
              Get personalized training at your convenience
            </p>
          </a>
          <a
            href="/park-classes"
            className="group rounded-xl border border-green-200 bg-white p-6 shadow-sm transition hover:shadow-md"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 text-green-600">
              <MapPin className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900 group-hover:text-green-600">
              Book a Class in Your Nearest Park
            </h3>
            <p className="text-sm text-gray-600">
              Join outdoor fitness sessions in your area
            </p>
          </a>
          <a
            href="/nutritionist-discovery"
            className="group rounded-xl border border-purple-200 bg-white p-6 shadow-sm transition hover:shadow-md"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900 group-hover:text-purple-600">
              Book a Nutritionist for Home
            </h3>
            <p className="text-sm text-gray-600">
              Personalized nutrition plans tailored to you
            </p>
          </a>
        </div>
      </section>

      <div className="mx-auto flex max-w-6xl flex-col gap-14 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
        <section aria-labelledby="categories-heading" className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionHeader
              id="categories-heading"
              label="Explore Categories"
              title="Fitness Solutions For Every Goal"
              className="mb-0"
            />
            <CTAButton
              href="/pricing"
              variant="outline"
              className="text-sm px-5 py-2"
              ariaLabel="View special offers and pricing"
            >
              Special Offers
              <PhoneCall className="h-4 w-4" aria-hidden="true" />
            </CTAButton>
          </div>
          <div className="flex flex-wrap gap-3" role="list">
            {categories.map((item) => (
              <span
                key={item}
                role="listitem"
                className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                tabIndex={0}
              >
                {item}
              </span>
            ))}
          </div>
        </section>

        <section aria-labelledby="programs-heading" className="space-y-8">
          <div className="section-divider" />
          <div className="flex flex-col gap-3">
            <SectionHeader
              id="programs-heading"
              label="Top Programs"
              title="Popular Programs"
              className="mb-0"
            />
            <div className="flex items-center gap-2 text-sm text-gray-700" aria-label="Rating: 4.9 stars">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" aria-hidden="true" />
              <span>4.9 Star Rated Programs</span>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2" role="list">
            {bestsellers.map((product) => (
              <article
                key={product.title}
                role="listitem"
                className="flex flex-col rounded-3xl border border-blue-200 bg-white/90 p-5 shadow-[0_20px_70px_rgba(59,130,246,0.1)] transition hover:shadow-[0_20px_70px_rgba(59,130,246,0.2)] focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2"
              >
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="h-52 w-full overflow-hidden rounded-2xl lg:w-1/2">
                    <img
                      src={product.image}
                      alt={`${product.title} - ${product.priceRange}`}
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex w-full flex-col justify-between">
                    <div className="space-y-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-700">
                        Popular
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                      </span>
                      <h3 className="font-display text-xl text-gray-900">
                        {product.title}
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {product.description}
                      </p>
                      <p className="text-sm text-gray-600 font-medium" aria-label={`Price: ${product.priceRange}`}>
                        {product.priceRange}
                      </p>
                      <div className="flex flex-wrap gap-2" role="list">
                        {product.variants.map((variant) => (
                          <span
                            key={variant}
                            role="listitem"
                            className="rounded-full border border-blue-300 px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50"
                          >
                            {variant}
                          </span>
                        ))}
                      </div>
                      {product.benefits && (
                        <ul className="flex flex-wrap gap-2 text-xs text-gray-600" role="list">
                          {product.benefits.map((benefit) => (
                            <li key={benefit} role="listitem" className="flex items-center gap-1">
                              <span className="text-blue-600">✓</span>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="mt-4 flex items-center gap-4 text-sm text-gray-700">
                      <CTAButton
                        href="/programs"
                        variant="primary"
                        className="text-sm px-4 py-2"
                        ariaLabel={`Get started with ${product.title}`}
                      >
                        Get Started
                        <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                      </CTAButton>
                      <a
                        href="/programs"
                        className="text-blue-600 underline-offset-4 transition hover:underline focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded"
                        aria-label={`View details for ${product.title}`}
                      >
                        View Details
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="about-heading"
          className="rounded-[32px] border border-blue-200 bg-white/80 p-8 shadow-[0_30px_80px_rgba(59,130,246,0.1)]"
        >
          <div className="grid gap-10 lg:grid-cols-2">
            <div className="space-y-6">
              <SectionHeader
                id="about-heading"
                label="About Us"
                title="Built With Passion. Driven By Results. Trusted By Thousands."
                className="mb-0"
              />
              <p className="text-lg leading-relaxed text-gray-700">
                FitEasy is revolutionizing how people approach fitness and wellness. We've built a comprehensive platform that eliminates the guesswork from your health journey. By combining cutting-edge technology with real human expertise, we deliver personalized workout plans, evidence-based nutrition programs, and holistic wellness solutions that adapt to your unique lifestyle, schedule, and goals. Our mission is simple: make professional fitness guidance accessible, affordable, and effective for everyone.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <ShieldCheck className="h-6 w-6 text-blue-600" />
                  <h3 className="mt-3 font-semibold text-gray-900">
                    Expert Guidance
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Every program is designed by certified fitness professionals and registered nutritionists with years of experience. We ensure all workouts follow safety protocols, progressive overload principles, and are tailored to your current fitness level, goals, and any physical limitations you may have.
                  </p>
                </div>
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <Users className="h-6 w-6 text-blue-600" />
                  <h3 className="mt-3 font-semibold text-gray-900">
                    Supportive Community
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Join over 100,000 members on their fitness journey. Our community features include progress sharing, group challenges, success stories, and peer support. Connect with others who share similar goals, celebrate milestones together, and stay motivated through the ups and downs of your transformation.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-[28px] border border-blue-300 bg-gradient-to-br from-blue-600 to-blue-800 p-6 text-white">
              <div className="space-y-4">
                <p className="text-xs uppercase tracking-[0.4em] text-blue-200">
                  Our Mission
                </p>
                <h3 className="font-display text-2xl">
                  Your fitness journey, simplified and personalized
                </h3>
                <p className="text-sm text-white/80">
                  We believe everyone deserves access to professional fitness guidance, regardless of their fitness level, budget, or schedule. No matter where you're starting from—whether you're a complete beginner or a seasoned athlete—we're here to help you achieve your goals with science-backed programs, personalized coaching, and unwavering support every step of the way.
                </p>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-white/15 bg-white/10 p-4 text-center"
                  >
                    <p className="text-3xl font-semibold">{stat.label}</p>
                    <p className="text-sm uppercase tracking-widest text-white/70">
                      {stat.helper}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm text-white/75">
                <Sparkles className="h-4 w-4 text-blue-200" />
                Real-time progress tracking, AI-powered insights, and personalized program adjustments based on your performance.
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="testimonials-heading" className="space-y-8">
          <SectionHeader
            id="testimonials-heading"
            label="Success Stories"
            title="Real Results From Real People"
            description="Hear from members who transformed their lives with FitEasy"
          />
          <div className="grid gap-6 md:grid-cols-3" role="list">
            {testimonials.map((testimonial) => (
              <figure
                key={testimonial.name}
                role="listitem"
                className="rounded-3xl border border-blue-200 bg-white/90 p-6 shadow-[0_25px_60px_rgba(59,130,246,0.1)] transition hover:shadow-[0_25px_60px_rgba(59,130,246,0.2)]"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" aria-hidden="true" />
                  ))}
                </div>
                <Quote className="h-6 w-6 text-blue-600" aria-hidden="true" />
                <blockquote className="mt-4 text-base leading-relaxed text-gray-700">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{testimonial.role}</p>
                  <p className="text-xs text-gray-500 mt-1">{testimonial.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section aria-labelledby="faq-heading" className="rounded-[32px] border border-blue-200 bg-white/85 p-8 shadow-[0_30px_80px_rgba(59,130,246,0.1)]">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <SectionHeader
                id="faq-heading"
                label="Frequently Asked"
                title="Everything You Need to Know"
                className="mb-6"
              />
              <div className="space-y-4" role="list">
                {faqs.map((faq) => (
                  <article
                    key={faq.question}
                    role="listitem"
                    className="rounded-2xl border border-blue-200 bg-blue-50 p-5 transition hover:shadow-md focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2"
                  >
                    <h3 className="font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="rounded-[28px] border border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100 p-6">
              <h3 className="font-display text-2xl text-gray-900">
                Get in Touch With Us
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Support • Bookings • Inquiries • Partnerships
              </p>
              <div className="mt-6 space-y-5" role="list">
                {contactInfo.map(({ icon: Icon, ...info }) => (
                  <a
                    key={info.label}
                    href={info.href}
                    role="listitem"
                    className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-white/80 p-4 transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                    aria-label={`${info.label}: ${info.value}`}
                  >
                    <Icon className="mt-1 h-5 w-5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-blue-700">
                        {info.label}
                      </p>
                      <p className="mt-1 font-semibold text-gray-900">
                        {info.value}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
              <div className="mt-6 rounded-2xl border border-dashed border-blue-300 p-4 text-sm text-gray-600">
                FitEasy Fitness Solutions
                <br />
                123 Fitness Avenue, Health City, HC 12345
                <br />
                Available 24/7 for your fitness journey
              </div>
            </div>
          </div>
        </section>

      </div>
      </main>

      <Footer />
    </div>
    </>
  );
}

